/**
 * Lightweight modal primitive — focus-trap-lite, backdrop click closes,
 * ESC closes. Prefers PD design over a heavy dialog lib.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v1
 */
import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';

export function Modal({
    open,
    title,
    subtitle,
    onClose,
    width = 560,
    footer,
    children
}: {
    open: boolean;
    title: string;
    subtitle?: string;
    onClose: () => void;
    width?: number;
    footer?: ReactNode;
    children: ReactNode;
}) {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div className="modal-backdrop" onClick={onClose}>
            <div
                className="modal"
                style={{ width }}
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
            >
                <header className="modal-header">
                    <div style={{ minWidth: 0 }}>
                        <h2 className="modal-title">{title}</h2>
                        {subtitle && <p className="modal-subtitle">{subtitle}</p>}
                    </div>
                    <button className="modal-close" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </header>
                <div className="modal-body">{children}</div>
                {footer && <footer className="modal-footer">{footer}</footer>}
            </div>
        </div>
    );
}
